import React, { useState, useEffect, useMemo } from 'react';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { Button, Spinner, Table, Tabs, Tab } from 'react-bootstrap';
import PageContainer from '../../layout/PageContainer'; // <-- IMPORT
// Sub-component for Peer Grouping
const PeerGrouping = () => {
    const [customers, setCustomers] = useState([]);
    const [transactions, setTransactions] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            setCustomers(await dbService.getAll('customers'));
            setTransactions(await dbService.getAll('transactions'));
        };
        fetchData();
    }, []);

    const peerGroups = useMemo(() => {
        const groups = {};
        for (const customer of customers) {
            const occupation = customer.occupation || 'Unknown';
            if (!groups[occupation]) {
                groups[occupation] = { customers: [], totalAmount: 0, totalTxns: 0 };
            }
            groups[occupation].customers.push(customer.id);
        }
        for (const txn of transactions) {
            const customer = customers.find(c => c.id === txn.customerId);
            const occupation = customer?.occupation || 'Unknown';
            if (groups[occupation]) {
                groups[occupation].totalAmount += txn.amount;
                groups[occupation].totalTxns++;
            }
        }
        return Object.entries(groups).map(([occupation, data]) => ({
            occupation,
            customerCount: data.customers.length,
            avgAmount: data.totalTxns > 0 ? (data.totalAmount / data.totalTxns).toFixed(2) : 0
        }));
    }, [customers, transactions]);

    return (
        <div>
            <h4>Customer Segmentation by Occupation</h4>
            <p>Analyze peer groups to identify outliers. For example, a "Software Engineer" with an average transaction amount significantly higher than their peer group might warrant investigation.</p>
            <Table striped bordered hover>
                <thead><tr><th>Peer Group (Occupation)</th><th># of Customers</th><th>Avg. Transaction Amount ($)</th></tr></thead>
                <tbody>
                    {peerGroups.map(group => (
                        <tr key={group.occupation}>
                            <td>{group.occupation}</td>
                            <td>{group.customerCount}</td>
                            <td>{parseFloat(group.avgAmount).toLocaleString()}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

// Sub-component for ML Model Management
const MlModelManager = () => {
    const [model, setModel] = useState(null);
    const [isTraining, setIsTraining] = useState(false);
    const { user } = useAuth();

    const fetchModel = async () => setModel(await dbService.getById('ml_models', 'txn-risk-model'));
    useEffect(() => { fetchModel(); }, []);

    const handleRetrain = async () => {
        setIsTraining(true);
        const allCases = await dbService.getAll('cases');
        const truePositives = allCases.filter(c => c.isTruePositive === true);

        if (truePositives.length === 0) {
            toast.info("No cases marked as 'True Positive' found to learn from.");
            setIsTraining(false);
            return;
        }

        // Simulate learning: for every true positive, the model gets slightly more sensitive
        const learningRate = 0.1;
        const newScoreAddition = Math.min(20, model.baseScoreAddition + (truePositives.length * learningRate)); // Cap at 20
        
        await dbService.update('ml_models', { ...model, baseScoreAddition: newScoreAddition });
        await dbService.addLog({ user: user.name, action: 'Retrained ML Model', details: `Learned from ${truePositives.length} cases.` });
        
        toast.success(`Model retrained! Base score is now ${newScoreAddition.toFixed(2)}.`);
        fetchModel();
        setIsTraining(false);
    };

    if (!model) return <Spinner animation="border" />;

    return (
        <div>
            <h4>Machine Learning Model Management</h4>
            <p>Simulate retraining your transaction anomaly detector based on analyst feedback. When analysts mark a closed case as a "True Positive", the model learns from it.</p>
            <div className="card">
                <div className="card-body">
                    <p><strong>Model Name:</strong> {model.name}</p>
                    <p><strong>Current Base Score Addition:</strong> {model.baseScoreAddition.toFixed(2)}</p>
                    <p className="text-muted">This score is added to an alert's base risk score from the rule engine. A higher value means the model is more sensitive.</p>
                    <Button onClick={handleRetrain} disabled={isTraining}>
                        {isTraining ? <Spinner as="span" size="sm" /> : 'Retrain Model from "True Positive" Cases'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const AutomationStudio = () => {
    return (
        <PageContainer title="Automation & AI Studio">
            <p>Configure and manage the system's intelligent automation and machine learning capabilities.</p>
            <Tabs defaultActiveKey="peer-grouping" className="mt-3">
                <Tab eventKey="peer-grouping" title="Peer Grouping & Segmentation">
                    <div className="p-3 border border-top-0"><PeerGrouping /></div>
                </Tab>
                <Tab eventKey="ml-model" title="ML Model Management">
                    <div className="p-3 border border-top-0"><MlModelManager /></div>
                </Tab>
            </Tabs>
        </PageContainer>
    );
};

export default AutomationStudio;